import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Archive, ArrowRight, GraduationCap, ArrowUpRight, Undo2, AlertTriangle, CheckCircle2 } from "lucide-react";

interface SchoolClass {
  id: string;
  name: string;
  sequence_order: number;
  leads_to_graduation: boolean | null;
  next_class_id: string | null;
  level_id: string | null;
  school_id: string | null;
  next_class: { id: string; name: string } | null;
  levels: { name: string } | null;
  student_count?: number;
  promoted_count?: number;
  unpromoted_count?: number;
}

export function ClassPromotionManager() {
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [archiveClassId, setArchiveClassId] = useState<string | null>(null);
  const [archiveClassName, setArchiveClassName] = useState<string>("");
  const [isArchiving, setIsArchiving] = useState(false);
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false);
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [promotionLog, setPromotionLog] = useState<string[]>([]);
  const [promotionProgress, setPromotionProgress] = useState(0);
  const [promotionTotal, setPromotionTotal] = useState(0);
  const [promotingSingleClass, setPromotingSingleClass] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: schools } = useQuery({
    queryKey: ["schools"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("schools")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: classes, isLoading } = useQuery({
    queryKey: ["promotion-classes", selectedSchool],
    queryFn: async () => {
      if (!selectedSchool) return [];

      const { data, error } = await supabase
        .from("school_classes")
        .select(`
          *,
          next_class:next_class_id(id, name),
          levels(name)
        `)
        .eq("school_id", selectedSchool)
        .order("sequence_order");

      if (error) throw error;

      // Get active student counts per class (split by promoted/unpromoted)
      // Paginate to avoid the 1000-row default limit
      let allStudents: { class: string | null; last_promoted_at: string | null; is_archived: boolean | null }[] = [];
      let page = 0;
      const pageSize = 1000;
      while (true) {
        const { data: batch, error: batchError } = await supabase
          .from("students")
          .select("class, last_promoted_at, is_archived")
          .eq("school_id", selectedSchool)
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (batchError) throw batchError;
        if (!batch || batch.length === 0) break;
        allStudents = allStudents.concat(batch);
        if (batch.length < pageSize) break;
        page++;
      }

      const countMap: Record<string, number> = {};
      const promotedMap: Record<string, number> = {};
      const unpromotedMap: Record<string, number> = {};
      let archivedCount = 0;
      allStudents.forEach((s) => {
        if (s.is_archived) {
          archivedCount++;
          return;
        }
        if (s.class) {
          countMap[s.class] = (countMap[s.class] || 0) + 1;
          if (s.last_promoted_at) {
            promotedMap[s.class] = (promotedMap[s.class] || 0) + 1;
          } else {
            unpromotedMap[s.class] = (unpromotedMap[s.class] || 0) + 1;
          }
        }
      });

      const classRows = (data as SchoolClass[]).map((cls) => {
        // For "ARCHIVED" classes, show the archived student count instead
        if (cls.name === "ARCHIVED") {
          return {
            ...cls,
            student_count: archivedCount,
            promoted_count: 0,
            unpromoted_count: 0,
          };
        }
        return {
          ...cls,
          student_count: countMap[cls.name] || 0,
          promoted_count: promotedMap[cls.name] || 0,
          unpromoted_count: unpromotedMap[cls.name] || 0,
        };
      });

      // Deduplicate: keep only one ARCHIVED row (the first one)
      const seenArchived = new Set<string>();
      const dedupedRows = classRows.filter((cls) => {
        if (cls.name === "ARCHIVED") {
          if (seenArchived.has("ARCHIVED")) return false;
          seenArchived.add("ARCHIVED");
        }
        return true;
      });

      return dedupedRows;
    },
    enabled: !!selectedSchool,
  });

  const archiveMutation = useMutation({
    mutationFn: async (className: string) => {
      setIsArchiving(true);
      try {
        const { data: studentsToArchive, error: fetchError } = await supabase
          .from("students")
          .select("id")
          .eq("school_id", selectedSchool)
          .eq("class", className)
          .eq("is_archived", false);

        if (fetchError) throw fetchError;

        if (!studentsToArchive || studentsToArchive.length === 0) {
          throw new Error("No students found in this class to archive");
        }

        const ids = studentsToArchive.map((s) => s.id);

        const { error: updateError } = await supabase
          .from("students")
          .update({
            is_archived: true,
            archived_at: new Date().toISOString(),
            archived_reason: "GRADUATION",
            last_promoted_at: new Date().toISOString(),
          })
          .in("id", ids);

        if (updateError) throw updateError;

        return studentsToArchive.length;
      } finally {
        setIsArchiving(false);
      }
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["promotion-classes"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      toast({
        title: "Success",
        description: `${count} student(s) in ${archiveClassName} have been archived (graduated).`,
      });
      setArchiveClassId(null);
      setArchiveClassName("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to archive students",
        variant: "destructive",
      });
      setArchiveClassId(null);
      setArchiveClassName("");
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async () => {
      setIsPromoting(true);
      setPromotionLog([]);
      setPromotionProgress(0);
      setPromotionTotal(0);
      try {
        if (!classes || classes.length === 0) throw new Error("No classes found");

        // PHASE 0: Auto-archive graduating class students first
        const graduatingClasses = classes.filter(
          (cls) => cls.leads_to_graduation && (cls.student_count ?? 0) > 0
        );

        if (graduatingClasses.length > 0) {
          setPromotionLog(["🎓 Archiving graduating class students first..."]);
          let totalArchived = 0;

          for (const gradCls of graduatingClasses) {
            const { data: studentsToArchive, error: fetchError } = await supabase
              .from("students")
              .select("id")
              .eq("school_id", selectedSchool)
              .eq("class", gradCls.name)
              .eq("is_archived", false);

            if (fetchError) {
              throw new Error(`Failed to fetch students in ${gradCls.name}: ${fetchError.message}`);
            }

            if (studentsToArchive && studentsToArchive.length > 0) {
              const ids = studentsToArchive.map((s) => s.id);
              const { error: archiveError } = await supabase
                .from("students")
                .update({
                  is_archived: true,
                  archived_at: new Date().toISOString(),
                  archived_reason: "GRADUATION",
                  last_promoted_at: new Date().toISOString(),
                })
                .in("id", ids);

              if (archiveError) {
                throw new Error(`Failed to archive ${gradCls.name}: ${archiveError.message}`);
              }

              totalArchived += studentsToArchive.length;
              setPromotionLog((prev) => [
                ...prev,
                `✅ ${gradCls.name}: ${studentsToArchive.length} student(s) archived (graduated)`,
              ]);
            }
          }

          if (totalArchived > 0) {
            setPromotionLog((prev) => [...prev, `🎓 ${totalArchived} student(s) archived in total.`, ""]);
          } else {
            setPromotionLog((prev) => [...prev, "⚪ No students to archive.", ""]);
          }
        }

        // PHASE 1: Pre-validate — fetch ALL students for ALL promotable classes
        const sortedClasses = [...classes]
          .filter((cls) => !cls.leads_to_graduation && cls.next_class)
          .sort((a, b) => b.sequence_order - a.sequence_order);

        if (sortedClasses.length === 0) throw new Error("No promotable classes found");

        setPromotionLog((prev) => [...prev, "🔍 Validating all classes before promotion..."]);
        const promotionPlan: { cls: SchoolClass; students: { id: string; age: number | null }[] }[] = [];

        for (const cls of sortedClasses) {
          const { data: studentsInClass, error: fetchError } = await supabase
            .from("students")
            .select("id, age")
            .eq("school_id", selectedSchool)
            .eq("class", cls.name)
            .eq("is_archived", false)
            .is("last_promoted_at", null);

          if (fetchError) {
            throw new Error(`Failed to read students in ${cls.name}: ${fetchError.message}`);
          }

          promotionPlan.push({
            cls,
            students: studentsInClass || [],
          });
        }

        const totalStudents = promotionPlan.reduce((sum, p) => sum + p.students.length, 0);
        if (totalStudents === 0) {
          setPromotionLog((prev) => [...prev, "⚠️ No students found to promote in any class."]);
          setIsPromoting(false);
          return 0;
        }

        setPromotionLog((prev) => [...prev, `✅ Validation complete: ${totalStudents} student(s) in ${promotionPlan.length} class(es) ready.`, ""]);

        // PHASE 2: Execute — batch update per class (processed highest sequence first to clear destinations)
        const totalClasses = promotionPlan.length;
        setPromotionTotal(totalClasses);
        let totalPromoted = 0;
        let classesProcessed = 0;
        const now = new Date().toISOString();

        for (const { cls, students } of promotionPlan) {
          const nextClassName = cls.next_class!.name;

          if (students.length === 0) {
            setPromotionLog((prev) => [...prev, `⚪ ${cls.name} → ${nextClassName}: 0 students (skipped)`]);
            classesProcessed++;
            setPromotionProgress(Math.round((classesProcessed / totalClasses) * 100));
            continue;
          }

          setPromotionLog((prev) => [...prev, `⏳ ${cls.name} → ${nextClassName}: ${students.length} student(s)...`]);

          const ids = students.map((s) => s.id);
          const { error: updateError } = await supabase
            .from("students")
            .update({
              class: nextClassName,
              last_promoted_at: now,
            })
            .in("id", ids);

          if (updateError) {
            setPromotionLog((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = `❌ ${cls.name} → ${nextClassName}: FAILED (${updateError.message})`;
              return [...updated, `🛑 Promotion stopped. Use "Undo Last Promotion" to revert already-promoted classes.`];
            });
            throw new Error(`Failed to promote ${cls.name}: ${updateError.message}`);
          }

          // Increment ages in a separate batch
          const ageGroups: Record<number, string[]> = {};
          for (const s of students) {
            if (s.age != null) {
              const newAge = s.age + 1;
              if (!ageGroups[newAge]) ageGroups[newAge] = [];
              ageGroups[newAge].push(s.id);
            }
          }
          for (const [newAge, groupIds] of Object.entries(ageGroups)) {
            const { error: ageError } = await supabase
              .from("students")
              .update({ age: parseInt(newAge) })
              .in("id", groupIds);
            if (ageError) {
              console.error("Age update error (non-fatal):", ageError);
            }
          }

          totalPromoted += students.length;
          setPromotionLog((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = `✅ ${cls.name} → ${nextClassName}: ${students.length} student(s) promoted`;
            return updated;
          });

          classesProcessed++;
          setPromotionProgress(Math.round((classesProcessed / totalClasses) * 100));
        }

        setPromotionLog((prev) => [...prev, "", `🎉 Done! ${totalPromoted} student(s) promoted in total.`]);
        return totalPromoted;
      } finally {
        setIsPromoting(false);
      }
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["promotion-classes"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      toast({
        title: "Promotion Complete",
        description: `${count} student(s) have been promoted to their next classes.`,
      });
      setShowPromoteConfirm(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to promote students",
        variant: "destructive",
      });
      setShowPromoteConfirm(false);
    },
  });

  const undoPromotionMutation = useMutation({
    mutationFn: async () => {
      setIsUndoing(true);
      try {
        if (!classes || classes.length === 0) throw new Error("No classes found");

        // Get ALL promoted students in this school
        const { data: promotedStudents, error: fetchError } = await supabase
          .from("students")
          .select("id, class, age, last_promoted_at")
          .eq("school_id", selectedSchool)
          .eq("is_archived", false)
          .not("last_promoted_at", "is", null);

        if (fetchError) throw fetchError;
        if (!promotedStudents || promotedStudents.length === 0) throw new Error("No promoted students found to undo");

        // Build reverse map: next_class_name -> original_class_name
        const reverseMap: Record<string, string> = {};
        for (const cls of classes) {
          if (!cls.leads_to_graduation && cls.next_class) {
            reverseMap[cls.next_class.name] = cls.name;
          }
        }

        // Process in DESCENDING sequence order (highest class first)
        // This ensures we move P.7→P.6 before P.6→P.5, preventing re-cascade
        const sortedClasses = [...classes]
          .filter((cls) => !cls.leads_to_graduation && cls.next_class)
          .sort((a, b) => b.sequence_order - a.sequence_order);

        let totalReverted = 0;

        for (const cls of sortedClasses) {
          const nextClassName = cls.next_class!.name;
          const studentsToRevert = promotedStudents.filter(
            (s) => s.class === nextClassName
          );

          if (studentsToRevert.length === 0) continue;

          // Batch update: move all students back to previous class and clear promotion flag
          const ids = studentsToRevert.map((s) => s.id);
          const { error: updateError } = await supabase
            .from("students")
            .update({
              class: cls.name,
              last_promoted_at: null,
            })
            .in("id", ids);

          if (updateError) throw updateError;

          // Batch decrement ages
          const ageGroups: Record<number, string[]> = {};
          for (const s of studentsToRevert) {
            if (s.age != null && s.age > 0) {
              const prevAge = s.age - 1;
              if (!ageGroups[prevAge]) ageGroups[prevAge] = [];
              ageGroups[prevAge].push(s.id);
            }
          }
          for (const [prevAge, groupIds] of Object.entries(ageGroups)) {
            await supabase
              .from("students")
              .update({ age: parseInt(prevAge) })
              .in("id", groupIds);
          }

          totalReverted += studentsToRevert.length;
        }

        return totalReverted;
      } finally {
        setIsUndoing(false);
      }
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["promotion-classes"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      toast({
        title: "Undo Complete",
        description: `${count} student(s) have been moved back to their previous classes.`,
      });
      setShowUndoConfirm(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to undo promotion",
        variant: "destructive",
      });
      setShowUndoConfirm(false);
    },
  });

  const singlePromoteMutation = useMutation({
    mutationFn: async (cls: SchoolClass) => {
      setPromotingSingleClass(cls.id);
      try {
        const nextClassName = cls.next_class!.name;

        const { data: studentsToPromote, error: fetchError } = await supabase
          .from("students")
          .select("id, age")
          .eq("school_id", selectedSchool)
          .eq("class", cls.name)
          .eq("is_archived", false)
          .is("last_promoted_at", null); // Only unpromoted students

        if (fetchError) throw fetchError;
        if (!studentsToPromote || studentsToPromote.length === 0) {
          throw new Error(`No unpromoted students found in ${cls.name}`);
        }

        const now = new Date().toISOString();
        const ids = studentsToPromote.map((s) => s.id);

        const { error: updateError } = await supabase
          .from("students")
          .update({ class: nextClassName, last_promoted_at: now })
          .in("id", ids);

        if (updateError) throw updateError;

        // Update ages in batches by current age
        const ageGroups: Record<number, string[]> = {};
        for (const s of studentsToPromote) {
          if (s.age != null) {
            const newAge = s.age + 1;
            if (!ageGroups[newAge]) ageGroups[newAge] = [];
            ageGroups[newAge].push(s.id);
          }
        }
        for (const [newAge, groupIds] of Object.entries(ageGroups)) {
          await supabase
            .from("students")
            .update({ age: parseInt(newAge) })
            .in("id", groupIds);
        }

        return { count: studentsToPromote.length, className: cls.name, nextClassName };
      } finally {
        setPromotingSingleClass(null);
      }
    },
    onSuccess: ({ count, className, nextClassName }) => {
      queryClient.invalidateQueries({ queryKey: ["promotion-classes"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["recently-promoted"] });
      toast({
        title: "Promotion Complete",
        description: `${count} student(s) promoted from ${className} → ${nextClassName}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to promote class",
        variant: "destructive",
      });
    },
  });

  const handleArchive = (classId: string, className: string) => {
    setArchiveClassId(classId);
    setArchiveClassName(className);
  };

  const hasPromotableStudents = classes?.some(
    (cls) => !cls.leads_to_graduation && cls.next_class && (cls.unpromoted_count ?? 0) > 0
  );

  // Check if there are recently promoted students
  const { data: recentlyPromoted } = useQuery({
    queryKey: ["recently-promoted", selectedSchool],
    queryFn: async () => {
      if (!selectedSchool) return null;
      const { data, error } = await supabase
        .from("students")
        .select("last_promoted_at")
        .eq("school_id", selectedSchool)
        .eq("is_archived", false)
        .not("last_promoted_at", "is", null)
        .order("last_promoted_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      return data && data.length > 0 ? data[0].last_promoted_at : null;
    },
    enabled: !!selectedSchool,
  });

  return (
    <div className="space-y-4">
      <Select value={selectedSchool} onValueChange={setSelectedSchool}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Select a school" />
        </SelectTrigger>
        <SelectContent>
          {schools?.map((school) => (
            <SelectItem key={school.id} value={school.id}>
              {school.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedSchool ? (
        isLoading ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : classes && classes.length > 0 ? (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Class</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Next Class</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {classes.map((cls) => (
                  <TableRow key={cls.id}>
                    <TableCell className="font-medium">{cls.name}</TableCell>
                    <TableCell>{cls.levels?.name || "-"}</TableCell>
                    <TableCell>
                      {cls.leads_to_graduation ? (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <GraduationCap className="h-4 w-4" />
                          Graduation
                        </span>
                      ) : cls.next_class ? (
                        <span className="flex items-center gap-1">
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          {cls.next_class.name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span>{cls.student_count}</span>
                        {(cls.promoted_count ?? 0) > 0 && (
                          <Badge variant="outline" className="text-xs flex items-center gap-0.5">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            {cls.promoted_count}
                          </Badge>
                        )}
                        {(cls.unpromoted_count ?? 0) > 0 && !cls.leads_to_graduation && cls.next_class && (
                          <Badge variant="outline" className="text-xs flex items-center gap-0.5">
                            <AlertTriangle className="h-3 w-3 text-amber-500" />
                            {cls.unpromoted_count}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {cls.leads_to_graduation ? (
                        <Badge variant="secondary">Graduating Class</Badge>
                      ) : cls.name === "ARCHIVED" ? (
                        <Badge variant="outline">Archive</Badge>
                      ) : (
                        <Badge variant="default">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {!cls.leads_to_graduation && cls.next_class && (cls.unpromoted_count ?? 0) > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => singlePromoteMutation.mutate(cls)}
                            disabled={promotingSingleClass === cls.id || isPromoting}
                          >
                            {promotingSingleClass === cls.id ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <ArrowUpRight className="h-4 w-4 mr-1" />
                            )}
                            Promote ({cls.unpromoted_count})
                          </Button>
                        )}
                        {cls.leads_to_graduation && (cls.student_count ?? 0) > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleArchive(cls.id, cls.name)}
                          >
                            <Archive className="h-4 w-4 mr-1" />
                            Archive ({cls.student_count})
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-4 border-t flex flex-wrap gap-2">
              {hasPromotableStudents && (
                <Button
                  onClick={() => setShowPromoteConfirm(true)}
                  className="w-full sm:w-auto"
                >
                  <ArrowUpRight className="h-4 w-4 mr-2" />
                  Promote All Students
                </Button>
              )}
              {recentlyPromoted && (
                <Button
                  variant="outline"
                  onClick={() => setShowUndoConfirm(true)}
                  className="w-full sm:w-auto"
                >
                  <Undo2 className="h-4 w-4 mr-2" />
                  Undo Last Promotion
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No classes found for this school. Add classes in the "Classes &
            Streams Management" section above.
          </div>
        )
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          Please select a school to view classes and manage student promotion.
        </div>
      )}

      <AlertDialog
        open={!!archiveClassId}
        onOpenChange={(open) => {
          if (!isArchiving) {
            setArchiveClassId(null);
            setArchiveClassName("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Graduating Students</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive all students in <strong>{archiveClassName}</strong> as
              graduated. This action cannot be undone. Are you sure you want to
              continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                archiveMutation.mutate(archiveClassName);
              }}
              disabled={isArchiving}
            >
              {isArchiving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Archiving...
                </>
              ) : (
                "Archive Students"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showPromoteConfirm}
        onOpenChange={(open) => {
          if (!isPromoting) setShowPromoteConfirm(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote All Students</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will first <strong>archive (graduate)</strong> all students in graduating classes,
                  then move remaining students to their next class based on the configured
                  progression paths.
                </p>
                {(isPromoting || promotionLog.length > 0) && (
                  <div className="space-y-2">
                    {promotionTotal > 0 && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Progress</span>
                          <span>{promotionProgress}%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary transition-all duration-300"
                            style={{ width: `${promotionProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="max-h-48 overflow-y-auto rounded-md border bg-muted/50 p-3 text-sm font-mono space-y-1">
                      {promotionLog.map((log, i) => (
                        <div key={i} className={log.startsWith("🎉") ? "font-bold text-primary" : ""}>{log}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPromoting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                promoteMutation.mutate();
              }}
              disabled={isPromoting}
            >
              {isPromoting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Promoting...
                </>
              ) : (
                "Promote Students"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showUndoConfirm}
        onOpenChange={(open) => {
          if (!isUndoing) setShowUndoConfirm(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo Last Promotion</AlertDialogTitle>
            <AlertDialogDescription>
              This will move all recently promoted students back to their previous classes
              and decrement their ages by one. Only the most recent batch promotion will be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUndoing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                undoPromotionMutation.mutate();
              }}
              disabled={isUndoing}
            >
              {isUndoing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Undoing...
                </>
              ) : (
                "Undo Promotion"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
